import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Alert,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Icon from 'react-native-vector-icons/Ionicons';
import { format, parseISO } from 'date-fns';

import { RootState, AppDispatch } from '@/store';
import {
  assignSub,
  clearSub,
  getSubCandidates,
  SubCandidate,
  SubCandidates,
} from '@/store/slices/timeOffSlice';
import { colors, font, space, radius } from '@/theme/chalkCourt';

interface Props {
  visible: boolean;
  requestId: number;
  eventId: number;
  currentSubId?: number | null;
  onClose: () => void;
  onAssigned: () => void;
  /**
   * 'record' = a manager documenting a sub for a past class (late-subs): saved as
   * already accepted with no push, so it confirms first with the payroll note.
   */
  mode?: 'assign' | 'record';
  /** The class date (yyyy-MM-dd), for the record-mode confirm copy. */
  eventDate?: string;
}

const fmtDay = (date?: string): string => {
  if (!date) {
    return 'that date';
  }
  try {
    return format(parseISO(date), 'EEE, MMM d');
  } catch {
    return date;
  }
};

const LOCATION_NAME: Record<string, string> = { SL: 'Southlake', RM: 'Rhome' };

const fullName = (c: SubCandidate): string =>
  `${c.employee.firstName ?? ''} ${c.employee.lastName ?? ''}`.trim() || `Employee ${c.employee.id}`;

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

/**
 * Location tag for a candidate relative to the class's location: the class's
 * location when they work there, the other location when they only work there,
 * nothing when either side is unknown.
 */
export const locationTag = (
  c: SubCandidate,
  eventLocation?: string | null
): { label: string; same: boolean } | null => {
  if (!eventLocation || c.sameLocation == null) {
    return null;
  }
  if (c.sameLocation) {
    return { label: LOCATION_NAME[eventLocation] ?? eventLocation, same: true };
  }
  const other = eventLocation === 'SL' ? 'RM' : eventLocation === 'RM' ? 'SL' : null;
  return other ? { label: LOCATION_NAME[other], same: false } : null;
};

/** "⚠ Teaching Team 4:30 4:30 PM–5:30 PM" */
export const conflictLine = (c: SubCandidate): string | null =>
  c.conflict ? `⚠ Teaching ${c.conflict.name} ${c.conflict.start}–${c.conflict.end}` : null;

/**
 * Picks (or clears) a substitute for one class. The list is the server's ranked
 * sub candidates — every active staff member except the requester, same
 * location first, then people free at that time. Someone with a clashing shift
 * is flagged (⚠) and can still be picked after an "Assign anyway?" confirm.
 * Name search filters the ranked list. The assign is reconciled server-side.
 */
const SubPickerModal: React.FC<Props> = ({
  visible,
  requestId,
  eventId,
  currentSubId,
  onClose,
  onAssigned,
  mode = 'assign',
  eventDate,
}) => {
  const recording = mode === 'record';
  const dispatch = useDispatch<AppDispatch>();
  const { isAssigning } = useSelector((state: RootState) => state.timeOff);

  const [query, setQuery] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [data, setData] = useState<SubCandidates | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Each load gets a token; only the latest one may update state. A response for a
  // class the picker is no longer showing (closed, or reopened on another class
  // before it arrived) is dropped.
  const loadToken = useRef(0);

  const load = useCallback(async () => {
    const token = ++loadToken.current;
    setLoading(true);
    setLoadError(null);
    const result = await dispatch(getSubCandidates({ id: requestId, eventId }));
    if (token !== loadToken.current) {
      return;
    }
    setLoading(false);
    if (getSubCandidates.fulfilled.match(result)) {
      if (result.payload?.event?.id != null && result.payload.event.id !== eventId) {
        return; // not the class this picker is open for
      }
      setData(result.payload);
    } else {
      setData(null);
      setLoadError((result.payload as string) || 'Could not load staff.');
    }
  }, [dispatch, requestId, eventId]);

  useEffect(() => {
    if (visible) {
      setQuery('');
      setSubmitError(null);
      setData(null);
      load();
    } else {
      loadToken.current += 1; // closing invalidates any in-flight load
    }
  }, [visible, load]);

  const filtered = useMemo(() => {
    const list = data?.candidates ?? [];
    const q = query.trim().toLowerCase();
    return q ? list.filter((c) => fullName(c).toLowerCase().includes(q)) : list;
  }, [data, query]);

  const assign = async (subEmployeeId: number) => {
    setSubmitError(null);
    const result = await dispatch(assignSub({ id: requestId, eventId, subEmployeeId }));
    if (assignSub.fulfilled.match(result)) {
      onAssigned();
      onClose();
    } else {
      setSubmitError((result.payload as string) || 'Could not assign a sub.');
    }
  };

  const pick = (c: SubCandidate) => {
    if (c.employee.id === currentSubId) {
      return; // already the sub — nothing to change
    }
    if (recording) {
      const clash = c.conflict
        ? ` They were teaching ${c.conflict.name} (${c.conflict.start}–${c.conflict.end}) then.`
        : '';
      Alert.alert(
        'Record sub?',
        `Recording a past sub adds a paid shift for ${fullName(c)} on ${fmtDay(eventDate)}. Payroll for that period may need to be re-run.${clash}`,
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Record sub', onPress: () => assign(c.employee.id) },
        ]
      );
      return;
    }
    if (!c.conflict) {
      assign(c.employee.id);
      return;
    }
    Alert.alert(
      'Assign anyway?',
      `${fullName(c)} is teaching ${c.conflict.name} (${c.conflict.start}–${c.conflict.end}) at that time.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Assign anyway', onPress: () => assign(c.employee.id) },
      ]
    );
  };

  const remove = () => {
    if (!recording) {
      doRemove();
      return;
    }
    Alert.alert(
      'Clear recorded sub?',
      `This removes the paid shift recorded on ${fmtDay(eventDate)}.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Clear', style: 'destructive', onPress: () => doRemove() },
      ]
    );
  };

  const doRemove = async () => {
    setSubmitError(null);
    const result = await dispatch(clearSub({ id: requestId, eventId }));
    if (clearSub.fulfilled.match(result)) {
      onAssigned();
      onClose();
    } else {
      setSubmitError((result.payload as string) || 'Could not remove the sub.');
    }
  };

  const event = data?.event;

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <View style={styles.handle} />
          <View style={styles.headRow}>
            <View style={styles.headText}>
              <Text style={styles.title}>{recording ? 'RECORD A SUB' : 'PICK A SUB'}</Text>
              {!!event && (
                <Text style={styles.eventLine} numberOfLines={1}>
                  {event.name} · {event.start}–{event.end}
                </Text>
              )}
            </View>
            <TouchableOpacity
              onPress={onClose}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              accessibilityLabel="Close sub picker">
              <Icon name="close" size={24} color={colors.inkSoft} />
            </TouchableOpacity>
          </View>

          <View style={styles.searchBox}>
            <Icon name="search" size={18} color={colors.inkFaint} />
            <TextInput
              style={styles.searchInput}
              value={query}
              onChangeText={setQuery}
              placeholder="Search staff…"
              placeholderTextColor={colors.inkFaint}
              autoCapitalize="none"
            />
          </View>

          {currentSubId != null && (
            <TouchableOpacity
              style={styles.removeRow}
              activeOpacity={0.7}
              disabled={isAssigning}
              onPress={remove}>
              <Icon name="close-circle" size={20} color={colors.accentDark} />
              <Text style={styles.removeText}>
                {recording ? 'Clear recorded sub' : 'Remove current sub'}
              </Text>
            </TouchableOpacity>
          )}

          {submitError && <Text style={styles.submitError}>{submitError}</Text>}

          {loading && !data ? (
            <View style={styles.centered}>
              <ActivityIndicator color={colors.accent} />
            </View>
          ) : loadError && !data ? (
            <View style={styles.centered}>
              <Text style={styles.errorText}>{loadError}</Text>
              <TouchableOpacity style={styles.retry} onPress={load} activeOpacity={0.7}>
                <Text style={styles.retryText}>Try again</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
              {filtered.length === 0 ? (
                <Text style={styles.noMatches}>
                  {query.trim() ? `No staff match "${query}".` : 'No staff available.'}
                </Text>
              ) : (
                filtered.map((c) => {
                  const name = fullName(c);
                  const isCurrent = c.employee.id === currentSubId;
                  const tag = locationTag(c, data?.eventLocation);
                  const clash = conflictLine(c);
                  return (
                    <TouchableOpacity
                      key={c.employee.id}
                      style={[styles.row, isCurrent && styles.rowCurrent]}
                      activeOpacity={0.7}
                      disabled={isAssigning || isCurrent}
                      onPress={() => pick(c)}
                      accessibilityState={{ disabled: isAssigning || isCurrent, selected: isCurrent }}
                      accessibilityLabel={`${name}${isCurrent ? ', current sub' : ''}${tag ? `, ${tag.label}` : ''}${clash ? ', has a conflicting class' : ''}`}>
                      <View style={styles.avatar}>
                        <Text style={styles.avatarText}>{initials(name)}</Text>
                      </View>
                      <View style={styles.rowBody}>
                        <View style={styles.nameRow}>
                          <Text style={styles.rowTitle} numberOfLines={1}>
                            {name}
                          </Text>
                          {tag && (
                            <View style={[styles.tag, tag.same ? styles.tagSame : styles.tagOther]}>
                              <Text style={[styles.tagText, tag.same && styles.tagTextSame]}>
                                {tag.label}
                              </Text>
                            </View>
                          )}
                        </View>
                        {isCurrent ? (
                          <Text style={styles.currentText}>Current sub</Text>
                        ) : (
                          !!clash && <Text style={styles.conflictText}>{clash}</Text>
                        )}
                      </View>
                      {isCurrent && (
                        <Icon name="checkmark-circle" size={22} color={colors.court} />
                      )}
                    </TouchableOpacity>
                  );
                })
              )}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28,26,18,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.bg,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    paddingHorizontal: space.lg,
    paddingBottom: space.xl,
    height: '80%',
  },
  handle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.lineStrong,
    marginTop: space.sm,
    marginBottom: space.md,
  },
  headRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: space.md,
  },
  title: {
    fontFamily: font.display,
    fontSize: 26,
    color: colors.ink,
    letterSpacing: -0.5,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: space.md,
    marginBottom: space.sm,
  },
  searchInput: {
    flex: 1,
    height: 44,
    fontFamily: font.body,
    fontSize: 15,
    color: colors.ink,
    marginLeft: space.sm,
  },
  removeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: space.sm,
    marginBottom: space.xs,
  },
  removeText: {
    fontFamily: font.bodySemi,
    fontSize: 14,
    color: colors.accentDark,
    marginLeft: space.xs,
  },
  submitError: {
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.accentDark,
    marginBottom: space.sm,
  },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  errorText: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
    padding: space.lg,
    textAlign: 'center',
  },
  list: { flex: 1 },
  listContent: { paddingTop: space.sm, paddingBottom: space.lg },
  noMatches: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkFaint,
    textAlign: 'center',
    paddingVertical: space.lg,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.sm,
    borderWidth: 1,
    borderColor: colors.line,
  },
  rowCurrent: {
    borderColor: colors.court,
    backgroundColor: colors.courtSoft,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.surfaceSunk,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: space.md,
  },
  avatarText: {
    fontFamily: font.bodyBold,
    fontSize: 14,
    color: colors.inkSoft,
  },
  rowBody: { flex: 1 },
  rowTitle: {
    fontFamily: font.bodyBold,
    fontSize: 15,
    color: colors.ink,
    flexShrink: 1,
  },
  headText: { flex: 1, marginRight: space.md },
  eventLine: {
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.inkSoft,
    marginTop: 2,
  },
  nameRow: { flexDirection: 'row', alignItems: 'center' },
  tag: {
    borderRadius: radius.pill,
    paddingVertical: 2,
    paddingHorizontal: 8,
    marginLeft: space.sm,
  },
  tagSame: { backgroundColor: colors.courtSoft },
  tagOther: { backgroundColor: colors.surfaceSunk },
  tagText: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    color: colors.inkSoft,
  },
  tagTextSame: { color: colors.court },
  conflictText: {
    fontFamily: font.bodySemi,
    fontSize: 12,
    color: colors.warn,
    marginTop: 2,
  },
  currentText: {
    fontFamily: font.bodySemi,
    fontSize: 12,
    color: colors.court,
    marginTop: 2,
  },
  retry: { minHeight: 44, justifyContent: 'center' },
  retryText: {
    fontFamily: font.bodySemi,
    fontSize: 14,
    color: colors.accentDark,
  },
});

export default SubPickerModal;
